export type SpecAxis = {

  name: string;

  values: string[];
};

export type SpecValues = Record<string, string>;

export type SpecEntry = { name: string; value: string };

export const SPEC_TEXT_SEPARATOR = " / ";

export const MAX_SPEC_AXES = 3;

const KEY_UNIT = "\u001f";
const KEY_PAIR = "\u001e";

export function normalizeSpecEntries(value: unknown): SpecEntry[] {
  if (!value || typeof value !== "object") {
    return [];
  }

  const raw: Array<[unknown, unknown]> = Array.isArray(value)
    ? value.map((item) => {
        const entry = (item ?? {}) as Record<string, unknown>;

        return [entry.name, entry.value];
      })
    : Object.entries(value as Record<string, unknown>);

  const seen = new Set<string>();
  const out: SpecEntry[] = [];
  for (const [rawName, rawValue] of raw) {
    const name = typeof rawName === "string" ? rawName.trim() : "";
    const text = typeof rawValue === "string" ? rawValue.trim() : "";
    if (!name || !text || seen.has(name)) continue;
    seen.add(name);
    out.push({ name, value: text });
  }

  return out;
}

export function normalizeSpecValues(value: unknown): SpecValues {

  return Object.fromEntries(normalizeSpecEntries(value).map((entry) => [entry.name, entry.value]));
}

export function toSpecEntries(
  specs: SpecValues,
  axes: SpecAxis[],
): SpecEntry[] {
  return axes
    .filter((axis) => Boolean(specs[axis.name]))
    .map((axis) => ({ name: axis.name, value: specs[axis.name]! }));
}

export function buildSpecText(specs: SpecValues, axes: SpecAxis[]): string {
  return axes
    .map((axis) => specs[axis.name])
    .filter((value): value is string => Boolean(value))
    .join(SPEC_TEXT_SEPARATOR);
}

export function axesFromSkus(
  skus: Array<{ specs?: unknown }>,
): SpecAxis[] {
  const order: string[] = [];
  const valuesByAxis = new Map<string, string[]>();

  for (const sku of skus) {

    for (const { name, value } of normalizeSpecEntries(sku.specs)) {
      let values = valuesByAxis.get(name);
      if (!values) {
        values = [];
        valuesByAxis.set(name, values);
        order.push(name);
      }
      if (!values.includes(value)) {
        values.push(value);
      }
    }
  }

  return order.map((name) => ({ name, values: valuesByAxis.get(name) ?? [] }));
}

export function normalizeSpecAxes(axes: SpecAxis[]): SpecAxis[] {
  const seenNames = new Set<string>();
  const out: SpecAxis[] = [];

  for (const axis of axes) {
    const name = axis.name.trim();
    if (!name || seenNames.has(name)) continue;
    seenNames.add(name);

    const seenValues = new Set<string>();
    const values: string[] = [];
    for (const raw of axis.values) {
      const value = raw.trim();
      if (!value || seenValues.has(value)) continue;
      seenValues.add(value);
      values.push(value);
    }
    if (values.length > 0) {
      out.push({ name, values });
    }
  }

  return out.slice(0, MAX_SPEC_AXES);
}

export function validateSpecAxes(axes: SpecAxis[]): string | null {
  if (axes.length > MAX_SPEC_AXES) {
    return `最多 ${MAX_SPEC_AXES} 个规格类`;
  }

  const seenNames = new Set<string>();
  for (const axis of axes) {
    const name = axis.name.trim();
    if (!name) {
      return "规格类名称不能为空";
    }
    if (seenNames.has(name)) {
      return `规格类「${name}」重复`;
    }
    seenNames.add(name);

    const values = axis.values.map((value) => value.trim()).filter(Boolean);
    if (values.length === 0) {
      return `规格类「${name}」至少要有一个规格值`;
    }
    const seenValues = new Set<string>();
    for (const value of values) {
      if (seenValues.has(value)) {
        return `规格类「${name}」下的「${value}」重复`;
      }
      seenValues.add(value);
    }
  }

  return null;
}

export function expandSpecCombinations(axes: SpecAxis[]): SpecValues[] {
  const normalized = normalizeSpecAxes(axes);
  if (normalized.length === 0) {
    return [];
  }

  let result: SpecValues[] = [{}];
  for (const axis of normalized) {
    const next: SpecValues[] = [];
    for (const combo of result) {
      for (const value of axis.values) {
        next.push({ ...combo, [axis.name]: value });
      }
    }
    result = next;
  }

  return result;
}

export function specKey(specs: SpecValues, axes: SpecAxis[]): string {
  return axes
    .map((axis) => `${axis.name}${KEY_UNIT}${specs[axis.name] ?? ""}`)
    .join(KEY_PAIR);
}

export function rebuildSkusBySpecs<
  T extends { id: string; specs?: unknown; specText: string },
>(
  axes: SpecAxis[],
  existing: T[],
  createRow: (specs: SpecValues, specText: string) => T,
): T[] {
  const normalized = normalizeSpecAxes(axes);
  if (normalized.length === 0) {
    return existing;
  }

  const previous = new Map<string, T>();
  for (const row of existing) {
    const key = specKey(normalizeSpecValues(row.specs), normalized);
    if (!previous.has(key)) {
      previous.set(key, row);
    }
  }

  return expandSpecCombinations(normalized).map((specs) => {
    const specText = buildSpecText(specs, normalized);
    const kept = previous.get(specKey(specs, normalized));

    return kept ? { ...kept, specs, specText } : createRow(specs, specText);
  });
}

export function isSpecValueAvailable(
  axisName: string,
  value: string,
  selected: SpecValues,
  skus: Array<{ specs?: unknown }>,
): boolean {

  const constraints = Object.entries(selected).filter(
    ([name]) => name !== axisName,
  );

  return skus.some((sku) => {
    const specs = normalizeSpecValues(sku.specs);
    if (specs[axisName] !== value) return false;

    return constraints.every(([name, wanted]) => specs[name] === wanted);
  });
}

export function selectSpecValue(
  axes: SpecAxis[],
  selected: SpecValues,
  axisName: string,
  value: string,
  skus: Array<{ specs?: unknown }>,
): SpecValues {
  const normalized = normalizeSpecAxes(axes);
  const next: SpecValues = { ...selected, [axisName]: value };

  for (const axis of normalized) {
    if (axis.name === axisName) continue;
    const current = next[axis.name];
    if (!current) continue;

    const stillPossible = skus.some((sku) => {
      const specs = normalizeSpecValues(sku.specs);

      return specs[axisName] === value && specs[axis.name] === current;
    });
    if (!stillPossible) {
      delete next[axis.name];
    }
  }

  return next;
}

export function specSignature(specs: SpecValues): string {
  return Object.keys(specs)
    .sort()
    .map((name) => `${name}${KEY_UNIT}${specs[name]}`)
    .join(KEY_PAIR);
}

export function reconcileSkusWithAxes<
  T extends { id: string; specs?: unknown; specText: string },
>(input: {
  prevAxes: SpecAxis[];
  nextAxes: SpecAxis[];
  skus: T[];
  createRow: (specs: SpecValues, specText: string) => T;
  restore?: (signature: string) => T | undefined;
}): { skus: T[]; removed: T[] } {
  const prev = normalizeSpecAxes(input.prevAxes);
  const next = normalizeSpecAxes(input.nextAxes);

  const renamedAxis = new Map<string, string>();
  const renamedValue = new Map<string, Map<string, string>>();

  next.forEach((axis, index) => {
    const before = prev[index];
    if (!before) return;

    if (before.name !== axis.name) {
      renamedAxis.set(before.name, axis.name);
    }
    if (before.values.length !== axis.values.length) return;

    axis.values.forEach((value, valueIndex) => {
      const previousValue = before.values[valueIndex];
      if (previousValue === undefined || previousValue === value) return;

      let map = renamedValue.get(axis.name);
      if (!map) {
        map = new Map<string, string>();
        renamedValue.set(axis.name, map);
      }
      map.set(previousValue, value);
    });
  });

  const liveNames = new Set(next.map((axis) => axis.name));
  const migrated = input.skus.map((sku) => {
    const specs: SpecValues = {};
    for (const entry of normalizeSpecEntries(sku.specs)) {
      const name = renamedAxis.get(entry.name) ?? entry.name;
      if (!liveNames.has(name)) continue;
      specs[name] = renamedValue.get(name)?.get(entry.value) ?? entry.value;
    }

    return { row: sku, specs };
  });

  if (next.length === 0) {
    return { skus: [], removed: input.skus };
  }

  const bySignature = new Map<string, { row: T; specs: SpecValues }>();
  for (const item of migrated) {
    const signature = specSignature(item.specs);
    if (!bySignature.has(signature)) {
      bySignature.set(signature, item);
    }
  }

  const picked = new Set<T>();
  const skus = expandSpecCombinations(next).map((specs) => {
    const signature = specSignature(specs);
    const specText = buildSpecText(specs, next);
    const kept = bySignature.get(signature);
    if (kept) {
      picked.add(kept.row);

      return { ...kept.row, specs, specText };
    }

    const revived = input.restore?.(signature);

    return revived
      ? { ...revived, specs, specText }
      : input.createRow(specs, specText);
  });

  const removed = migrated
    .filter((item) => !picked.has(item.row))
    .map((item) => item.row);

  return { skus, removed };
}

export function findSkuBySpecs<T extends { specs?: unknown }>(
  selected: SpecValues,
  axes: SpecAxis[],
  skus: T[],
): T | null {
  const normalized = normalizeSpecAxes(axes);
  if (normalized.length === 0) {
    return null;
  }
  if (normalized.some((axis) => !selected[axis.name])) {
    return null;
  }

  const wanted = specKey(selected, normalized);

  return (
    skus.find(
      (sku) => specKey(normalizeSpecValues(sku.specs), normalized) === wanted,
    ) ?? null
  );
}

export function orderSpecAxes(live: SpecAxis[], order: unknown): SpecAxis[] {
  const preferred = readSpecOrder(order);
  const byName = new Map(live.map((axis) => [axis.name, axis]));
  const names = [...new Set([...preferred.map((axis) => axis.name), ...live.map((axis) => axis.name)])];
  return names.flatMap((name) => {
    const axis = byName.get(name);
    if (!axis) return [];
    const wanted = preferred.find((item) => item.name === name)?.values ?? [];
    return [{ name, values: [...new Set([...wanted.filter((value) => axis.values.includes(value)), ...axis.values])] }];
  });
}

export function readSpecOrder(value: unknown): SpecAxis[] {
  if (!Array.isArray(value)) return [];
  return normalizeSpecAxes(value.slice(0, MAX_SPEC_AXES).flatMap((item) => {
    if (!item || typeof item !== "object" || typeof item.name !== "string" || !Array.isArray(item.values)) return [];
    return [{ name: item.name, values: item.values.filter((text: unknown): text is string => typeof text === "string").slice(0, 20) }];
  }));
}

export function validateSpecOrder(value: unknown): string | null {
  if (value === undefined) return null;
  if (!Array.isArray(value) || value.some((axis) => !axis || typeof axis.name !== "string" || !Array.isArray(axis.values) || axis.values.some((text: unknown) => typeof text !== "string"))) return "规格顺序格式无效";
  const invalid = validateSpecAxes(value);
  if (invalid) return invalid;
  for (const axis of value) {
    if (axis.values.length > 20) return "每个规格类最多20个规格值";
    if (axis.values.some((text: string) => !text.trim())) return `请填写或删除「${axis.name.trim()}」中的空白规格值`;
  }
  return null;
}

export type EditableSpecAxis = SpecAxis & { id: string; valueIds: string[] };

export function reconcileEditableSpecAxes<T extends { id: string; specs?: unknown; specText: string }>(input: {
  prevAxes: EditableSpecAxis[]; nextAxes: EditableSpecAxis[]; skus: T[];
  createRow: (specs: SpecValues, specText: string) => T;
  restore?: (signature: string) => T | undefined;
}): { skus: T[]; removed: T[] } {
  const prev = input.prevAxes, next = input.nextAxes;
  const normalized = normalizeSpecAxes(next);
  if (!normalized.length) {

    return prev.length === 0
      ? { skus: input.skus, removed: [] }
      : { skus: [], removed: input.skus };
  }
  const onlyValueOrder = prev.length === next.length && next.every((axis, index) => {
    const old = prev[index];
    return old.id === axis.id && old.name.trim() === axis.name.trim()
      && old.values.length === axis.values.length
      && axis.valueIds.every((id, i) => {
        const before = old.valueIds.indexOf(id);
        return before >= 0 && old.values[before].trim() === axis.values[i].trim();
      });
  });
  if (onlyValueOrder) {

    const ranked = input.skus.map((row) => {
      const specs = normalizeSpecValues(row.specs);
      return { row, ranks: normalized.map((axis) => {
        const index = axis.values.indexOf(specs[axis.name]);
        return index < 0 ? axis.values.length : index;
      }) };
    });
    ranked.sort((a, b) => {
      for (let i = 0; i < normalized.length; i++) {
        const difference = a.ranks[i] - b.ranks[i];
        if (difference) return difference;
      }
      return 0;
    });
    return { skus: ranked.map(({ row }) => row), removed: [] };
  }
  const structural = prev.length !== next.length || next.some((axis) => !prev.some((old) => old.id === axis.id));
  const added = new Map<string, Set<string>>();
  for (const axis of next) {
    const old = prev.find((item) => item.id === axis.id);
    const values = axis.values.filter((_, index) => !old?.valueIds.includes(axis.valueIds[index]));
    added.set(axis.name.trim(), new Set(values.map((value) => value.trim())));
  }
  const migrated = new Map<string, { row: T; specs: SpecValues }>();
  for (const row of input.skus) {
    const original = normalizeSpecValues(row.specs);
    const entries: Array<[string, string]> = [];
    let removed = false;
    for (const old of prev) {
      const axis = next.find((item) => item.id === old.id);
      if (!axis) continue;
      const index = old.values.findIndex((text) => text.trim() === original[old.name.trim()]);
      const target = index < 0 ? -1 : axis.valueIds.indexOf(old.valueIds[index]);
      if (target < 0) { removed = true; break; }
      entries.push([axis.name.trim(), axis.values[target].trim()]);
    }
    if (!removed) {
      const specs = Object.fromEntries(entries);
      const signature = specSignature(specs);
      if (!migrated.has(signature)) migrated.set(signature, { row, specs });
    }
  }
  const picked = new Set<T>();
  const skus = expandSpecCombinations(normalized).flatMap((specs) => {
    const signature = specSignature(specs), kept = migrated.get(signature);
    if (kept) { picked.add(kept.row); return [{ ...kept.row, specs, specText: buildSpecText(specs, normalized) }]; }
    if (!structural && !Object.entries(specs).some(([name, value]) => added.get(name)?.has(value))) return [];
    const restored = input.restore?.(signature);
    return [restored ? { ...restored, specs, specText: buildSpecText(specs, normalized) } : input.createRow(specs, buildSpecText(specs, normalized))];
  });
  return { skus, removed: input.skus.filter((row) => !picked.has(row)) };
}

export function translatedSpecAxes(order: SpecAxis[], source: Array<{ id: string; specs: unknown }>, translated: Array<{ id: string; specs: unknown }>): SpecAxis[] {
  const translatedById = new Map(translated.map((row) => [row.id, normalizeSpecEntries(row.specs)]));
  const labels = new Map<string, Map<string, Array<{ name: string; value: string }>>>();
  for (const row of source) {
    const translatedEntries = translatedById.get(row.id);
    normalizeSpecEntries(row.specs).forEach((entry, index) => {
      const target = translatedEntries?.[index];
      if (!target) return;
      let values = labels.get(entry.name);
      if (!values) { values = new Map(); labels.set(entry.name, values); }
      const targets = values.get(entry.value) ?? [];
      if (!targets.some((item) => item.name === target.name && item.value === target.value)) targets.push(target);
      values.set(entry.value, targets);
    });
  }
  const preferred = new Map<string, string[]>();
  for (const axis of order) for (const value of axis.values) for (const entry of labels.get(axis.name)?.get(value) ?? []) {
    const values = preferred.get(entry.name) ?? [];
    if (!values.includes(entry.value)) values.push(entry.value);
    preferred.set(entry.name, values);
  }
  return orderSpecAxes(axesFromSkus(translated), Array.from(preferred, ([name, values]) => ({ name, values })));
}
